import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { Section, Row } from './settingsPrimitives';
import { setDevicePref } from '../services/syncStore';
import { personalScopeId } from '../services/personalScope';
import { refreshVault, refreshVaultMaster } from '../store/vaultStore';
import { setWorkspaceReloadNonce } from '../store/workspaceStore';

// Matches device_migration.rs's own `DECLINE_PREF_KEY` constant exactly —
// this is the row `local_handoff_decline` writes and `local_handoff_status`'s
// short-circuit reads (both keyed on `personal:<uid>`, same as
// `personalScopeId()` above once signed in).
const DECLINE_PREF_KEY = 'local_handoff_declined';

/**
 * The opt-in local-to-account hand-off offer
 * (plans/29_07_2026/vault-storage-transition/REQ-local-to-account-handoff.md
 * §1; TSW3-MIGRATION.md §3.5.2/§12 Step 4's UI). Settings → Account,
 * signed-in only (the tab that mounts this is itself `authOnly`).
 *
 * **Never automatic.** Login never calls any of the three `local_handoff_*`
 * commands — this component's own mount-time read is the only thing that
 * ever asks, and neither `accept` nor `decline` fires without the user
 * clicking a button here. Nothing above this component (App.js, the login
 * flow) knows this exists.
 *
 * **Declining is a stable, terminal state**, not an error and not a prompt
 * that returns on its own — `local_handoff_status` reports it durably
 * (`device_prefs`, phase 4's `local_handoff_decline`) until the user
 * explicitly changes their mind. So this renders a quiet row even after
 * declining, with a way back in, rather than disappearing — a hidden control
 * is not a "reachable" one (REQ §1: "the user can change their mind later").
 *
 * **Un-declining.** `local_handoff_accept` itself never touches the decline
 * flag (only `local_handoff_decline` writes it and only `local_handoff_status`'s
 * short-circuit reads it — `device_migration.rs`'s `DECLINE_PREF_KEY`), so a
 * successful "Bring it in now" from the Declined row must clear it here or
 * every future status read keeps reporting `Declined` forever, even though
 * the data was already brought in (phase 7 review, major finding). There is
 * no dedicated "un-decline" command, but there does not need to be one —
 * TSW3-MIGRATION.md §3.5.6 names the already-generic
 * `workspace_db_set_pref(scope, key, None)` as exactly this mechanism, which
 * is what `doAccept` calls below.
 *
 * **Only a boolean/count, never the diff table.** REQ §2.1's "Settings → Sync
 * with Cloud" (a per-entity-kind table with a one-click push) is a distinct,
 * larger feature, explicitly foreseen but explicitly not built by this
 * phase — this component is only the offer/accept/decline REQ §1 asks for.
 */
export default function VaultHandoffSection() {
  const { t } = useTranslation();
  // HandoffOffer | null (not yet read). `{ state: 'Offered', local_only_count }
  // | { state: 'Declined' } | { state: 'NothingToOffer' } | { state: 'NotSignedIn' }`
  // — snake_case field name because `device_migration.rs`'s `HandoffOffer`
  // has no `#[serde(rename_all = "camelCase")]` (unlike most vault_* structs
  // — read the actual struct before assuming the usual convention here).
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The last completed action's outcome, shown once. Kept even after a
  // reload flips `offer` to `NothingToOffer` — a silent disappearance right
  // after "Bring it in" would look like the click did nothing.
  const [result, setResult] = useState(null);

  const reload = useCallback(async () => {
    if (!isTauri()) return;
    try {
      const next = await invoke('local_handoff_status');
      setOffer(next);
    } catch (e) {
      console.warn('[vault] local_handoff_status failed:', e?.message || e);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!isTauri()) return null;

  const state = offer?.state;
  // Nothing to say: never signed in, or nothing has ever accumulated
  // signed-out on this device. `!result` matters here — right after
  // accepting everything there was, status legitimately becomes
  // `NothingToOffer`, and the confirmation below must still render once.
  if (!offer || state === 'NotSignedIn' || (state === 'NothingToOffer' && !result)) {
    return null;
  }

  const doAccept = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const outcome = await invoke('local_handoff_accept');
      // Un-decline (module doc comment): best-effort, and unconditional
      // rather than gated on `state === 'Declined'` — a stale/never-set row
      // is a harmless no-op DELETE, and this is the only place that ever
      // needs to clear it. Failure here is logged, not surfaced: the data
      // was already moved, so an error banner over that would be a lie.
      try {
        await setDevicePref(personalScopeId(), DECLINE_PREF_KEY, null);
      } catch (e) {
        console.warn('[vault] clearing local_handoff_declined failed:', e?.message || e);
      }
      setResult(outcome || {});
      // The accept can rename an entirely different SQLite file onto this
      // account (FirstHandoff) or merge rows into the already-open one
      // (MergeHandoff) — either way the vault items, workspace list and
      // collections tree already on screen were loaded before this click and
      // nothing else re-fetches them just because this succeeded (phase 7
      // review, major finding). `reload()` below only re-reads the offer
      // status, not any of this.
      refreshVault();
      refreshVaultMaster();
      setWorkspaceReloadNonce((n) => (Number.isFinite(n) ? n + 1 : 1));
      await reload();
    } catch (e) {
      setError(
        t('vault.handoffAcceptFailed', 'Could not bring in your local data. Try again.')
      );
    }
    setBusy(false);
  };

  const doDecline = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await invoke('local_handoff_decline');
      await reload();
    } catch (e) {
      setError(t('vault.handoffDeclineFailed', 'That did not work. Try again.'));
    }
    setBusy(false);
  };

  const resultText = () => {
    if (!result) return '';
    const moved = (Number(result.workspace_rows_merged) || 0) + (Number(result.personal_docs_merged) || 0);
    if (moved === 0) {
      return t('vault.handoffNothingMoved', 'Nothing to bring in — your account already has it all.');
    }
    const base = t(
      'vault.handoffMoved',
      'Done — brought in {{count}} item(s) from before you signed in.',
      { count: moved }
    );
    // Blocker A (vault-storage-transition, unchanged by this phase): a vault
    // is never merged, only ever carried along by a FIRST hand-off renaming
    // the whole file. An account that already had ANY vault of its own keeps
    // its local passwords/SFTP keys/notes in `_local`, untouched — silently
    // leaving that out here would read as "everything moved" when it did not.
    return result.vault_left_orphaned_in_local
      ? `${base} ${t(
          'vault.handoffVaultOrphaned',
          'Your passwords and other vault items from before were not moved — your account already has its own vault.'
        )}`
      : base;
  };

  // Rendered independently, not as an if/else on `state`: right after a
  // successful "Bring it in" the reload can legitimately land on
  // `NothingToOffer` (everything there was is now moved) — in that case
  // NEITHER row below is the right thing to show, only the confirmation.
  return (
    <Section title={t('vault.handoffHeading', 'Local data')}>
      {state === 'Declined' && (
        <Row
          label={t('vault.handoffDeclinedLabel', "You haven't brought this in")}
          desc={t(
            'vault.handoffDeclinedHint',
            "You chose not to bring in the data you made before signing in. It's still on this device, and nothing was deleted."
          )}
        >
          <button
            type="button"
            className="settings-action-btn"
            disabled={busy}
            onClick={doAccept}
          >
            {busy
              ? t('vault.handoffBusy', 'Working…')
              : t('vault.handoffReconsider', 'Bring it in now')}
          </button>
        </Row>
      )}
      {state === 'Offered' && (
        <Row
          label={t('vault.handoffOfferedLabel', 'Bring in your local data')}
          desc={t(
            'vault.handoffOfferedHint',
            '{{count}} item(s) you saved before signing in only exist on this device. Bring them into your account so they sync and back up.',
            { count: Number(offer.local_only_count) || 0 }
          )}
        >
          <div className="flex items-center flex-shrink-0" style={{ gap: 8 }}>
            <button
              type="button"
              className="settings-action-btn"
              disabled={busy}
              onClick={doDecline}
            >
              {t('vault.handoffDecline', 'Not now')}
            </button>
            <button
              type="button"
              className="settings-action-btn"
              disabled={busy}
              onClick={doAccept}
            >
              {busy ? t('vault.handoffBusy', 'Working…') : t('vault.handoffAccept', 'Bring it in')}
            </button>
          </div>
        </Row>
      )}
      {result && (
        <div className="settings-row-desc" role="status" style={{ marginTop: 4 }}>
          {resultText()}
        </div>
      )}
      {error && (
        <p className="text-xs" role="alert" style={{ color: 'var(--danger, #ef4444)', margin: '6px 0 0' }}>
          {error}
        </p>
      )}
    </Section>
  );
}
